export type TreatmentApproach = {
    key: string;
    monday_key: string;
    title: string;
    link: string;
    duration: string;
    appointments: string;
    nb_aligners: string;
    benefits: { [key: string]: string };
};

export type TreatmentFeature = {
    label: string;
    intensity: number;
};

export type TreatmentDetails = {
    key: string;
    monday_key: string;
    label: string;
    description: string;
    features: TreatmentFeature[];
    approaches_description: string;
    approaches: TreatmentApproach[];
    learnMoreLink: string;
    approachesTitle: string;
    disclaimer?: string;
};

const pediatric_treatment_details: TreatmentDetails = {
    key: 'pediatrics',
    monday_key: 'Pediatrics',
    label: 'Pediatric',
    description:
        'All pediatric cases are planned with arch expansion in mind and may include Phase 0 (permanent dentition) or Phase 1 (mixed dentition)',
    features: [
        { label: 'IPR', intensity: 0 },
        { label: 'Dental Monitoring', intensity: 4 },
        { label: 'Bite turbos/ramps', intensity: 2 },
        { label: 'Attachments', intensity: 2 },
        { label: 'Mandibular advancement', intensity: 2 }
    ],
    learnMoreLink: 'https://youtu.be/czwTZ-jpOf8',
    approaches_description:
        'Each higher-level treatment option includes all requirements from the previous level. It has been noted where there is a material increase in mechanics (e.g., added attachments, auxiliaries, or appliances).',
    approachesTitle: 'Pediatric Orthodontics',
    approaches: [
        {
            key: 'pediatrics-need-one',
            monday_key: 'Pediatrics 1',
            title: 'Phase 0 (primary dentition only)',
            link: 'https://youtu.be/2hANwrfa6FE',
            // link: 'https://youtu.be/1l13MdDJmEI',
            duration: '12-16 months',
            appointments: '~4-6',
            nb_aligners: '~30-45',
            benefits: {
                'Planned Mechanics': 'Arch expansion, protraction and coordination.',
                'Treatment Commitments':
                    '~12-16 months duration, 4-6 patient appointments, attachments, possible A/P correction'
            }
        },
        {
            key: 'pediatrics-need-two',
            monday_key: 'Pediatrics 2',
            title: 'Phase 1 (early to mixed dentition)',
            link: 'https://youtu.be/0n7pQ411rxg',
            // link: 'https://youtu.be/8xIQHmDyTv4',
            duration: '18-24 months',
            appointments: '~5-7',
            nb_aligners: '~50-70',
            benefits: {
                'Planned Mechanics':
                    'Arch expansion, protraction and coordination, eruption compensation/leveling and aligning, mandibular advancement if indicated (moderate to significant skeletal discrepancy)',
                'Treatment Commitments':
                    '18-24 months duration, 5-7 patient appointments, more attachments, Caplin hooks & elastics potentially, Orthopulse optional'
            }
        }
    ]
};

const airway_treatment_details: TreatmentDetails = {
    key: 'airway',
    monday_key: 'Airway',
    label: 'Airway',
    description:
        'Emphasizes mesialization with a comprehensive approach focused on permanent dentition, arch expansion, and avoiding retraction.',
    features: [
        { label: 'IPR', intensity: 2 },
        { label: 'Dental Monitoring', intensity: 4 },
        { label: 'Bite turbos/ramps', intensity: 2 },
        { label: 'Attachments', intensity: 4 },
        { label: 'Mandibular advancement', intensity: 4 }
    ],
    learnMoreLink: 'https://youtu.be/JuBVAPjMJPE',
    approaches_description:
        'Each higher-level treatment option includes all requirements from the previous level. It has been noted where there is a material increase in mechanics (e.g., added attachments, auxiliaries, or appliances)',
    approaches: [
        {
            key: 'airway-need-one',
            monday_key: 'Airway 1',
            title: 'Airway-Focused Level 1 (Mild)',
            link: 'https://youtu.be/ihQb7KpNA3E',
            // link: 'https://youtu.be/RMTcTtbQET4',
            duration: '12 months',
            appointments: '~3-5',
            nb_aligners: '~30-40',
            benefits: {
                'Planned Mechanics':
                    'No anterior retraction, posterior mesialization, nominal expansion',
                'Treatment Commitments':
                    '~12 months duration, 3-5 patient appointments, attachments, caplin hooks & elastics potentially, orthopulse optional'
            }
        },
        {
            key: 'airway-need-two',
            monday_key: 'Airway 2',
            title: 'Airway-Focused Level 2 (Moderate)',
            link: 'https://youtu.be/XqCQCVm62YA',
            // link: 'https://youtu.be/9lTb_1bnnbE',
            duration: '12-18 months',
            appointments: '~4-6',
            nb_aligners: '~30-45',
            benefits: {
                'Planned Mechanics':
                    'No anterior retraction, posterior mesialization, moderate expansion',
                'Treatment Commitments':
                    '12–18 months duration, 4-6 patient appointments, more attachments, Caplin hooks & elastics likely, Orthopulse likely'
            }
        },
        {
            key: 'airway-need-three',
            monday_key: 'Airway 3',
            title: 'Airway-Focused Level 3 (Significant)',
            link: 'https://youtu.be/Tc6JY24a2YY',
            // link: 'https://youtu.be/sNMTKRkaW6o',
            duration: '18-24 months',
            appointments: '~5-7',
            nb_aligners: '~50-70',
            benefits: {
                'Planned Mechanics':
                    'No anterior retraction, posterior mesialization, maximum expansion (mindful of roots and cortical plate), maximum anchorage protection.',
                'Treatment Commitments':
                    '18-24 months duration, 5-7 patient appointments, complex mechanics, Caplin hooks mandatory with elastics, Orthopulse mandatory.'
            }
        }
    ],
    approachesTitle: 'Airway-Focused',
    disclaimer:
        'Any time elastics are removed, time will increase, as less teeth will be moved together at the same time.'
};

const traditional_treatment_details: TreatmentDetails = {
    key: 'traditional',
    monday_key: 'Traditional Ortho',
    label: 'Traditional Ortho',
    description: 'Focuses on resolving crowding through expansion, with retraction permitted.',
    features: [
        { label: 'IPR', intensity: 2 },
        { label: 'Dental Monitoring', intensity: 4 },
        { label: 'Bite turbos/ramps', intensity: 2 },
        { label: 'Attachments', intensity: 2 },
        { label: 'Mandibular advancement', intensity: 2 }
    ],
    learnMoreLink: 'https://youtu.be/ba-rMnSXiGA',
    approaches_description:
        'Each higher-level treatment option includes all requirements from the previous level. It has been noted where there is a material increase in mechanics (e.g., added attachments, auxiliaries, or appliances).',
    approachesTitle: 'Comprehensive Orthodontics',
    approaches: [
        {
            key: 'traditional-need-one',
            monday_key: 'Traditional',
            title: 'Traditional ortho',
            link: 'https://youtu.be/ba-rMnSXiGA',
            // link: 'https://youtu.be/IwT610acaUw',
            duration: '12-18 months',
            appointments: '3-6',
            nb_aligners: '~30-45',
            benefits: {
                'Planned Mechanics':
                    'Arch expansion as necessary to resolve crowding, elastic protocols as necessary, distalization as an option',
                'Treatment Commitments':
                    '~12-18 months duration, 3-6 patient appointments, attachments, potential IPR, orthopulse (dependent on severity of case)'
            }
        }
    ]
};

const tmd_treatment_details: TreatmentDetails = {
    key: 'tmd',
    monday_key: 'TMD',
    label: 'TMD',
    description:
        'TMD cases focus on reducing pain, joint strain and disfunction of the temporomandibular joint and surrounding muscles.',
    features: [
        { label: 'IPR', intensity: 2 },
        { label: 'Dental Monitoring', intensity: 4 },
        { label: 'Bite turbos/ramps', intensity: 3 },
        { label: 'Attachments', intensity: 4 },
        { label: 'Mandibular advancement', intensity: 4 }
    ],
    learnMoreLink: 'https://youtu.be/KfNCBNu6Lg4',
    approaches_description:
        'Each higher-level treatment option includes all requirements from the previous level. It has been noted where there is a material increase in mechanics (e.g., added attachments, auxiliaries, or appliances).',
    approachesTitle: 'TMD Focused',
    approaches: [
        {
            key: 'tmd-need-one',
            monday_key: 'TMD 1',
            title: 'TMD Level 1 (Mild)',
            link: 'https://youtu.be/Pf5SSInV2p4',
            // link: 'https://youtu.be/T3ViajuOSO0',
            duration: '12-18 months',
            appointments: '3-6',
            nb_aligners: '~30-45',
            benefits: {
                'Planned Mechanics':
                    'Mild increase in vertical dimension, non retraction, removal of anterior interference and arch coordination allowing more comfortable condylar position',
                'Treatment Commitments':
                    '~12-18 months duration, ~4-6 patient appointments, attachments, Caplin hooks & elastics potentially, Orthopulse likely'
            }
        },
        {
            key: 'tmd-need-two',
            monday_key: 'TMD 2',
            title: 'TMD Level 2 (Moderate)',
            link: 'https://youtu.be/-PFPS6GSaxk',
            // link: 'https://youtu.be/Xui_5uXzToQ',
            duration: '14-20 months',
            appointments: '~5-7',
            nb_aligners: '~40-60',
            benefits: {
                'Planned Mechanics':
                    'Significant increase in vertical dimension, requires posterior bite turbos or unified occlusal attachments',
                'Treatment Commitments':
                    '14-20 months duration, ~5-7 patient appointments, Caplin hooks & elastics mandatory, Orthopulse mandatory, occlusal buildups on second molars to determine vertical dimension'
            }
        },
        {
            key: 'tmd-need-three',
            monday_key: 'TMD 3',
            title: 'TMD Level 3 (Significant)',
            link: 'https://youtu.be/Olnt6SX-eIk',
            //link: 'https://youtu.be/jRJyH5QxfeY',
            duration: '18-24 months',
            appointments: '~6-8',
            nb_aligners: '~50-70', // rule here is *3 of duration
            benefits: {
                'Planned Mechanics':
                    'Maximum increase in vertical dimension, requires posterior bite turbos or unified occlusal attachments',
                'Treatment Commitments':
                    '18-24 months duration, ~6-8 patient appointments, Caplin hooks & elastics mandatory, Orthopulse mandatory, occlusal buildups on second molars to determine vertical dimension'
            }
        }
    ]
};

const treatmentDetails = {
    Pediatrics: pediatric_treatment_details,
    Airway: airway_treatment_details,
    'Traditional Ortho': traditional_treatment_details,
    TMD: tmd_treatment_details
};

function getCaseType(caseTypeKey: string): TreatmentDetails | undefined {
    return treatmentDetails[caseTypeKey as keyof typeof treatmentDetails];
}

function getSubCaseType(
    caseTypeKey: string,
    subCaseTypeKey: string
): TreatmentApproach | undefined {
    const details = treatmentDetails[caseTypeKey as keyof typeof treatmentDetails];
    if (!details || !Array.isArray(details.approaches)) {
        return undefined;
    }
    return details.approaches.find(
        (approach: TreatmentApproach) => approach.monday_key === subCaseTypeKey
    );
}

export { getCaseType, getSubCaseType, treatmentDetails };
